import { Form } from 'antd';
import { Card, Input, Select, Spin, Checkbox, AutoComplete, Button, Row, Col } from 'antd';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import { nameof } from '../../core/utils/object';
import { useParams } from 'react-router-dom';
import {
  CoreDiameter,
  DeviceType,
  deviceTypeLabels,
  deviceTypeSorter,
  getLicenseToolCode,
  isHourBasedLicense,
  LicenseCreationAttributesStore,
  LicenseDataItem,
  LicenseDataItemStore,
  LicenseDataSummaryStore,
} from '../../stores/licensing-store';
import { setHeader, setRoutes } from '../../core/ui/shell';
import moment from 'moment';
import { useIdentity } from '../../core/services/authentication';
import { getCurrentOrg, getDateFormat } from '../../common/utils';
import ShowLicenseDialog from '../licenses/license-show';
import { IdentityType, PermissionsConstants } from 'src/common/types';
import DatePicker from 'src/core/ui/date-components/date-picker';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { useCheckGlobalLicensesPermission } from 'src/common/hooks/use-check-global-licenses-permission';

const { RangePicker } = DatePicker;

const permissions = [PermissionsConstants.ApproveLicenses, PermissionsConstants.ManageLicenses];

const LicenseInfoPage: FC = () => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const params = useParams();
  const [form] = Form.useForm();
  let licenseDataId = params.id!;
  const currentOrg = getCurrentOrg(identity);
  const currentUser = identity.profile['sub'];
  const inputRef = useRef();
  const licenseItemStore = container.get(LicenseDataItemStore);
  const licenseItemState = licenseItemStore.state;
  const licenseStateValue = licenseItemState.item;
  const licenseDataStore = container.get(LicenseDataSummaryStore);
  const licenseDataState = licenseDataStore.state;
  const isBusy = licenseItemState.isBusy;
  const [projectOptions] = useState<string[]>([]);
  const [customerOptions] = useState<string[]>([]);
  const [canCreateUnlimitedLicense, setCanCreateUnlimitedLicense] = useState<boolean>();
  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesState = licenseCreationAttributesStore.state;
  const [licenseVisible, setLicenseVisible] = useState(false);
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType>();

  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const hasManageLicensesPermissions = !checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManageLicenses);

  const [hasGlobalLicenseManagerPermission, checkingGlobalLicenseManagerPermission] = useCheckGlobalLicensesPermission();

  const canApproveLicenses =
    !checkingPermissions &&
    !checkingGlobalLicenseManagerPermission &&
    (userPermissionsSet.has(PermissionsConstants.ApproveLicenses) || hasGlobalLicenseManagerPermission);

  console.log(hasGlobalLicenseManagerPermission);

  const { TextArea } = Input;
  useEffect(() => {
    licenseCreationAttributesStore.getByUserScope({ identityId: currentUser, scopeId: currentOrg.id, identityType: IdentityType.User }).then(() => {
      licenseItemStore.load(licenseDataId).then(() => {
        setCanCreateUnlimitedLicense(licenseItemState.item.value.unlimited);
        setSelectedDeviceType(licenseItemState.item.value.deviceType);
      });
    });
  }, [inputRef]); // eslint-disable-line react-hooks/exhaustive-deps

  if (licenseItemState.item.value) {
    setHeader(t('LicenseDetails'));

    setRoutes([
      { path: '', breadcrumbName: t('Home') },
      { path: 'licenses', breadcrumbName: t('Licenses') },
      {
        path: `licenses/${getLicenseToolCode(licenseItemState.item.value as LicenseDataItem)}`,
        breadcrumbName: `${getLicenseToolCode(licenseItemState.item.value as LicenseDataItem)}`,
      },
    ]);
  }

  const getDeviceOptions = () => {
    const deviceList = licenseCreationAttributesState.item.value
      ? licenseCreationAttributesState.item.value.allowedDevices
      : Object.values(DeviceType);

    return deviceList.toSorted(deviceTypeSorter).map((y) => {
      return { label: t(deviceTypeLabels[y]), value: y };
    });
  };

  const updateSignOff = () => {
    var currentValue = licenseStateValue.signedOffOn.value;
    licenseItemStore.updateSignOff({ signOff: currentValue === undefined }, licenseStateValue.id.value).then((response) => {
      if (response?.data?.isSuccess) {
        licenseItemStore.load(licenseDataId);
      }
    });
  };

  return (
    <Spin
      spinning={
        isBusy.get() || licenseDataState.isBusy.get() || licenseCreationAttributesState.isBusy.get() || canCreateUnlimitedLicense === undefined
      }
    >
      {licenseStateValue && licenseStateValue.value && licenseStateValue.id.value === licenseDataId && (
        <div>
          <Card style={{ padding: 40 }}>
            <Form form={form} layout="vertical" autoComplete="off">
              <Form form={form} layout="inline" autoComplete="off" style={{ marginBottom: 20 }}>
                <Row gutter={[8, 12]} style={{ width: '100%' }}>
                  <Col lg={6} xs={24}>
                    <Form.Item
                      name={nameof<LicenseDataItem>('deviceType')}
                      style={{ width: '100%' }}
                      initialValue={
                        !licenseCreationAttributesState.item.value ||
                        licenseCreationAttributesState.item.value.allowedDevices.includes(licenseStateValue.deviceType.value)
                          ? licenseStateValue.deviceType.value
                          : deviceTypeLabels[licenseStateValue.deviceType.value]
                      }
                      label={t('DeviceType')}
                    >
                      <Select style={{ width: '100%' }} placeholder={t('DeviceType')} options={getDeviceOptions()} disabled={true} />
                    </Form.Item>
                  </Col>

                  {(form.getFieldValue('deviceType') === DeviceType.SCTD ||
                    (form.getFieldValue('deviceType') === undefined && licenseStateValue.deviceType.value === DeviceType.SCTD)) && (
                    <Col lg={4} xs={24}>
                      <Form.Item
                        initialValue={
                          licenseStateValue.deviceType.value === DeviceType.SCTD ? parseInt(licenseStateValue.toolCode.value[0]) : undefined
                        }
                        label={t('ToolDiameter')}
                        name={'coreDiameter'}
                        rules={[{ required: true, message: t('PleaseEnterCoreDiameter') }]}
                        style={{ marginRight: 15 }}
                      >
                        <Select
                          disabled
                          style={{ height: '100%' }}
                          placeholder={t('ToolDiameter')}
                          options={Object.entries(CoreDiameter)
                            .filter((v) => isNaN(parseInt(v[0])))
                            .map(([key, value]) => ({ label: key, value }))}
                        />
                      </Form.Item>
                    </Col>
                  )}

                  <Col lg={6} xs={24}>
                    <Form.Item
                      name={nameof<LicenseDataItem>('toolCode')}
                      initialValue={getLicenseToolCode(licenseStateValue.value as LicenseDataItem)}
                      label={t('ToolCode')}
                    >
                      <Input style={{ width: '100%' }} autoComplete="off" allowClear placeholder={t('ToolCode')} disabled={true} />
                    </Form.Item>
                  </Col>

                  {(!licenseCreationAttributesState.item.value || licenseCreationAttributesState.item.value.canCreateUnlimitedLicense) && (
                    <Col xl={2}>
                      <Form.Item
                        name={nameof<LicenseDataItem>('unlimited')}
                        label={t('Unlimited')}
                        valuePropName="checked"
                        initialValue={licenseStateValue.unlimited.value}
                      >
                        <Checkbox disabled={true} />
                      </Form.Item>
                    </Col>
                  )}
                  {!canCreateUnlimitedLicense && (
                    <>
                      {!isHourBasedLicense(selectedDeviceType) && (
                        <Col xl={6}>
                          <Form.Item label={t('PeriodOfValidity')} style={{ width: '100%' }}>
                            {licenseItemState.item.value.unlimited && (
                              <RangePicker style={{ height: 40, width: '100%' }} format={getDateFormat()} disabled={true} />
                            )}
                            {!licenseItemState.item.value.unlimited && (
                              <RangePicker
                                style={{ height: 40, width: '100%' }}
                                format={getDateFormat()}
                                defaultValue={[moment(licenseStateValue.value.fromDate).utc(), moment(licenseStateValue.value.untilDate).utc()]}
                                disabled={true}
                              />
                            )}
                          </Form.Item>
                        </Col>
                      )}
                      {isHourBasedLicense(selectedDeviceType) && (
                        <Col xl={6}>
                          <Form.Item
                            name={nameof<LicenseDataItem>('workingHours')}
                            label={t('WorkingHours')}
                            rules={[{ required: !canCreateUnlimitedLicense, message: t('PleaseEnterWorkingHours') }]}
                            style={{ width: '100%' }}
                          >
                            <Input
                              disabled={true}
                              style={{ width: '100%' }}
                              autoComplete="off"
                              allowClear
                              placeholder={t('WorkingHours')}
                              defaultValue={
                                isHourBasedLicense(licenseItemState.item.value.deviceType) && !licenseItemState.item.value.unlimited
                                  ? licenseItemState.item.value.workingHours
                                  : undefined
                              }
                            />
                          </Form.Item>
                        </Col>
                      )}
                    </>
                  )}
                  <Col xl={12} sm={24} xs={24}>
                    <Form.Item name={nameof<LicenseDataItem>('project')} label={t('Project')} initialValue={licenseStateValue.project.value}>
                      <AutoComplete
                        style={{ width: '100%', height: '100%' }}
                        options={projectOptions.map((x) => {
                          return { value: x };
                        })}
                        //style={{ width: 400 }}
                        placeholder={t('Project')}
                        disabled={true}
                      />
                    </Form.Item>
                  </Col>
                  <Col xl={12} sm={24} xs={24}>
                    <Form.Item name={nameof<LicenseDataItem>('customer')} label={t('Customer')} initialValue={licenseStateValue.customer.value}>
                      <AutoComplete
                        style={{ width: '100%', height: '100%' }}
                        options={customerOptions.map((x) => {
                          return { value: x };
                        })}
                        // style={{ width: 400 }}
                        placeholder={t('Customer')}
                        disabled={true}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      style={{ width: 'calc(100%)' }}
                      name={nameof<LicenseDataItem>('notes')}
                      label={t('Notes')}
                      initialValue={licenseStateValue.notes.value}
                    >
                      <TextArea placeholder={t('Notes')} autoComplete="off" rows={3} disabled={true} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name={nameof<LicenseDataItem>('isRecurrent')}
                      valuePropName="checked"
                      initialValue={licenseStateValue.isRecurrent.value}
                    >
                      <Checkbox disabled>{t('Recurrence')}</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              {licenseStateValue.value.needsSignOff && canApproveLicenses && (
                <Button type="primary" onClick={() => updateSignOff()} style={{ margin: '10px' }}>
                  {licenseStateValue.value.signedOffByUserName ? t('Refuse') : t('SignOff')}
                </Button>
              )}
              {(!licenseStateValue.needsSignOff.value || licenseStateValue.signedOffByUserName.value) &&
                (hasGlobalLicenseManagerPermission || hasManageLicensesPermissions) && (
                  <Button type="default" onClick={() => setLicenseVisible(true)} style={{ margin: '10px' }}>
                    {t('ShowLicense')}
                  </Button>
                )}
            </Form>
          </Card>
        </div>
      )}
      {licenseVisible && (
        <ShowLicenseDialog
          licenseDataId={licenseDataId}
          onClose={() => {
            setLicenseVisible(false);
          }}
        />
      )}
    </Spin>
  );
};

export default LicenseInfoPage;
